import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthActions } from '@components/auth/auth.actions';
import { nutritionGuideActions } from './nutrition-guide.action';
import { DailyFoodLoggingType } from './nutrition-guide.interface';
import { ReportNourishGuidelineService } from './nutrition-guide.service';

/**
 * Nutrition Guide Effects.
 */
@Injectable()
export class NutritionGuideEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(ReportNourishGuidelineService);

  /**
   * Fetch Eligibility Generation Nourish Plus Effect.
   */
  fetchEligibilityGenerationNourishPlus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchEligibilityGenerationNourishPlus),
      switchMap(() =>
        this.service.getEligibilityGenerationNourishPlus().pipe(
          map((eligibility) =>
            nutritionGuideActions.fetchEligibilityGenerationNourishPlusSuccess({
              eligibility: eligibility.data
            })
          ),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchEligibilityGenerationNourishPlusFailure(
                {
                  error:
                    error.message ??
                    'Failed to fetch eligibility generation nourish plus'
                }
              )
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Food Restriction Effect.
   */
  fetchFoodRestriction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchFoodRestriction),
      switchMap(() =>
        this.service.foodRestriction().pipe(
          map((response) =>
            nutritionGuideActions.fetchFoodRestrictionSuccess({
              foodRestriction: response.results ?? []
            })
          ),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchFoodRestrictionFailure({
                error: error.message ?? 'Failed to fetch food restriction'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Food Source Effect.
   */
  fetchFoodSource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchFoodSource),
      switchMap(() =>
        this.service.foodSource().pipe(
          map((response) =>
            nutritionGuideActions.fetchFoodSourceSuccess({
              foodSource: response.results ?? []
            })
          ),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchFoodSourceFailure({
                error: error.message ?? 'Failed to fetch food source'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Home Overview Effect.
   */
  fetchHomeOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchHomeOverview),
      switchMap(({ params }) =>
        this.service.getOverview(params).pipe(
          map((response) => {
            return nutritionGuideActions.fetchHomeOverviewSuccess({
              overview: response.data as DailyFoodLoggingType
            });
          }),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchHomeOverviewFailure({
                error: error.message ?? 'Failed to fetch home overview'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Ideal Calorie Intake Effect.
   */
  fetchIdealCalorieIntake$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchIdealCalorieIntake),
      switchMap(() =>
        this.service.idealCalorieIntake().pipe(
          map((idealCalorieIntake) =>
            nutritionGuideActions.fetchIdealCalorieIntakeSuccess({
              idealCalorieIntake
            })
          ),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchIdealCalorieIntakeFailure({
                error: error.message ?? 'Failed to fetch ideal calorie intake'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Meal Plan Effect.
   */
  fetchMealPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchMealPlan),
      switchMap(() =>
        this.service.mealPlan().pipe(
          map((response) =>
            nutritionGuideActions.fetchMealPlanSuccess({
              mealPlan: response.results ?? []
            })
          ),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchMealPlanFailure({
                error: error.message ?? 'Failed to fetch meal plan'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Meal Type Effect.
   */
  fetchMealType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchMealType),
      switchMap(() =>
        this.service.getMealTypes({ isActive: true }).pipe(
          map((response) => {
            return nutritionGuideActions.fetchMealTypeSuccess({
              mealType: response.data?.mealTypes ?? []
            });
          }),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchMealTypeFailure({
                error: error.message ?? 'Failed to fetch meal plan'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Overview Effect.
   */
  fetchOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchOverview),
      switchMap(({ params }) =>
        this.service.getOverview(params).pipe(
          map((response) => {
            return nutritionGuideActions.fetchOverviewSuccess({
              overview: response.data as DailyFoodLoggingType
            });
          }),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchOverviewFailure({
                error: error.message ?? 'Failed to fetch overview'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Personal Information Effect.
   */
  fetchPersonalInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        nutritionGuideActions.fetchPersonalInformation,
        AuthActions.setProfile // Refetch when profile is set
      ),
      switchMap(() =>
        this.service.personalInformation().pipe(
          map((personalInformation) =>
            nutritionGuideActions.fetchPersonalInformationSuccess({
              personalInformation
            })
          ),
          catchError((error) =>
            of(
              nutritionGuideActions.fetchPersonalInformationFailure({
                error: error.message ?? 'Failed to fetch personal information'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Fetch Process Generation Nourish Plus Effect.
   */
  fetchProcessGenerationNourishPlus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(nutritionGuideActions.fetchProcessGenerationNourishPlus),
      switchMap(({ isWaitingProgressCompleted }) =>
        this.service
          .getProcessGenerationNourishPlus(isWaitingProgressCompleted)
          .pipe(
            map((process) =>
              nutritionGuideActions.fetchProcessGenerationNourishPlusSuccess({
                process: process.data
              })
            ),
            catchError((error) =>
              of(
                nutritionGuideActions.fetchProcessGenerationNourishPlusFailure({
                  error:
                    error.message ??
                    'Failed to fetch process generation nourish plus'
                })
              )
            )
          )
      )
    )
  );

  onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => nutritionGuideActions.reset())
      );
    },
    { dispatch: true }
  );
}
